// The admin screens that own how this model was set up: the audience it trained
// on, the conversion mapping behind what it predicts, and the push config behind
// where its scores go.
//
// It used to sit at the foot of the generated summary, which is where the Quick
// links block's navigation had gone when that block came off the page. The
// summary is gone and this is not generated: it is three deterministic links, so
// it belongs on the metadata card with the rest of the facts.
//
// Every URL goes through buildAdminUrl, so all three inherit the admin base URL
// webpack injects per build target and a staging build can only ever reach
// staging admins. No base configured means no row.
import React, { useEffect, useState } from 'react';

import { buildAdminUrl } from '../../utils/adminUrl';
import {
  ADMIN_PAGES_UNRESOLVED,
  adminSetupUrls,
  fetchAdminPagesEnabled,
} from '../../utils/adminSetupPages';
import './modelSetupLinks.css';

// The audience and conversion paths are not constants here: which generation of
// each screen this tenant can open is a flag the admin app owns, so they come
// from `adminSetupPages.ts`, which is also where the two generations are
// written down. The sidebar's Setup section resolves the same answer.
const PUSH_SETTINGS = '/settings/push';

export function ModelSetupLinks({ tenant }: { tenant: string }) {
  // Starts on the pages that need no flag and upgrades when the answer lands,
  // so the chips render at once and a click during the round trip still opens
  // something. Resolved after mount because this page is server-rendered.
  const [pages, setPages] = useState(ADMIN_PAGES_UNRESOLVED);

  useEffect(() => {
    let live = true;
    fetchAdminPagesEnabled(tenant).then((answer) => {
      if (live) setPages(answer);
    });
    return () => {
      live = false;
    };
  }, [tenant]);

  if (!NEW_ADMIN_BASE_URL) return null;

  const urls = adminSetupUrls(tenant, pages);

  // The list, always, and not this model's own audience.
  //
  // It used to deep link to `/mapping/audiences/<name>` for a model with a named
  // audience, on a page that has since been replaced. Guessing the detail route on the
  // page that replaced it would be betting on a URL nothing here can check, on the most
  // used of the three rows, and it would fail only where the audience is NAMED, which is
  // every real model and none of the test ones. So the row opens where the audiences
  // are. `audienceMappingPath`, the prop that fed it, and the `trainingAudience()` that
  // read three store pages to produce that prop went with it.
  const links: [string, string][] = [
    ['Audience', urls.audience],
    ['Conversion', urls.conversion],
    // Shown unconditionally. It used to appear only when the summary's exports
    // sentence came out non-null, which tied a link to a settings screen to
    // whether a model run had said anything about connectors.
    ['Export config', buildAdminUrl(tenant, PUSH_SETTINGS)],
  ];

  return (
    <div className="msl">
      {/* No heading. Three chips carrying an outward arrow already say they are
          somewhere else, and "Configured in Admin" above them was a label for a label:
          it took a whole line of the card to introduce three words. */}
      <div className="msl-links">
        {links.map(([label, href]) => (
          <a
            key={label}
            className="msl-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {label}
            {/* The character, not a FontAwesome glyph. The kit takes FA6 Pro
                names and a name it does not carry renders as nothing at all, so a
                new icon here is a thing that either works or silently disappears;
                this one is already on screen. */}
            <span aria-hidden className="msl-arrow">
              ↗
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

export default ModelSetupLinks;
